import { Cross2Icon } from '@radix-ui/react-icons';
import { Flex, Text } from '@radix-ui/themes';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { SessionRecapHelper } from 'classes/helpers/session-recap.helper';
import { LegendDot } from 'components/sections/analytics/session-hits/recap/legend-dot';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { DIVERGING_GRADIENT } from 'enums/session-recap/colors';
import { EXIT_VELO_SCALE, RecapColorBy } from 'enums/session-recap/enums';
import { t } from 'i18next';
import { ISessionRecapHit } from 'interfaces/session-recap/i-session-recap-hit';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useMemo } from 'react';

export const RecapColorLegend = (props: {
  colorBy: RecapColorBy;
  records: ISessionRecapHit[];
  /** null means every category is shown */
  activeKeys: string[] | null;
  onToggle: (key: string) => void;
  onClear: () => void;
  onHoverStart: (key: string) => void;
  onHoverEnd: (key: string) => void;
}) => {
  const sessionMachine = useAnalyticsStore((s) => s.sessionMachine);

  const items = useMemo(
    () => SessionRecapHelper.getLegendItems(props.records, props.colorBy),
    [props.colorBy, props.records]
  );

  if (!items) {
    const unit = MetricHelper.getSpeedUnit(sessionMachine?.isMetric);

    const format = (mph: number) =>
      (sessionMachine?.isMetric ? MetricHelper.mphToKph(mph) : mph).toFixed(0);

    return (
      <Flex
        justify="center"
        align="center"
        gap={RADIX.FLEX.GAP.SM}
        className="recap-legend"
      >
        <Text size="1" color={RADIX.COLOR.SECONDARY}>
          {`${format(EXIT_VELO_SCALE.min)} ${unit}`}
        </Text>
        <div
          className="recap-gradient-bar recap-gradient-bar-inline"
          style={{
            background: `linear-gradient(to right, ${DIVERGING_GRADIENT.join(
              ', '
            )})`,
          }}
        />
        <Text size="1" color={RADIX.COLOR.SECONDARY}>
          {`${format(EXIT_VELO_SCALE.max)}+ ${unit}`}
        </Text>
      </Flex>
    );
  }

  const filtering = props.activeKeys !== null;
  const active = new Set(props.activeKeys ?? items.map((item) => item.key));

  return (
    <Flex
      justify="center"
      align="center"
      gap={RADIX.FLEX.GAP.SM}
      wrap="wrap"
      className="recap-legend"
    >
      {items.map((item) => {
        const label = t(item.label);
        const isActive = active.has(item.key);

        return (
          <span
            key={item.key}
            className="recap-legend-item"
            data-active={isActive}
            data-filtering={filtering}
            onMouseEnter={() => props.onHoverStart(item.key)}
            onMouseLeave={() => props.onHoverEnd(item.key)}
          >
            <button
              type="button"
              className="recap-legend-toggle"
              aria-pressed={isActive}
              onClick={() => props.onToggle(item.key)}
            >
              <LegendDot color={item.color} />
              <Text size="1">{label}</Text>
            </button>
            {filtering && isActive && (
              <button
                type="button"
                className="recap-legend-remove"
                aria-label={t('hitters.recap-remove-x', {
                  x: label,
                }).toString()}
                onClick={() => props.onToggle(item.key)}
              >
                <Cross2Icon />
              </button>
            )}
          </span>
        );
      })}
      {filtering && (
        <button
          type="button"
          className="recap-legend-clear"
          onClick={props.onClear}
        >
          <Text size="1">{t('hitters.recap-clear')}</Text>
        </button>
      )}
    </Flex>
  );
};
